import React from 'react';
import { motion } from 'framer-motion';
import { Info, Lock, Share2, Globe, Mail, ShoppingBag, MapPin, UserCheck, ShieldCheck } from 'lucide-react';
import './ChafadiPrivacyPolicyPage.css';

const ChafadiPrivacyPolicyPage = () => {
    React.useEffect(() => {
        document.title = "Privacy Policy - Chafadi Marketplace";
        window.scrollTo(0, 0);
    }, []);

    const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    return (
        <div className="chafadi-policy-container">
            <motion.div 
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                className="chafadi-policy-content"
            >
                <header className="chafadi-policy-header">
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <h1>Privacy Policy for Chafadi</h1>
                        <p className="chafadi-policy-date">Updated: {currentDate}</p>
                    </motion.div>
                </header>

                <div className="chafadi-policy-intro">
                    <p>
                        Welcome to <strong>Chafadi</strong>, the premier digital marketplace for Niger. At 
                        Chafadi (available via <span className="chafadi-highlight">chafadi.com</span> and our mobile application), we are committed to protecting 
                        the privacy of our buyers and sellers.
                    </p>
                    <p style={{ marginTop: '1.5rem' }}>
                        This policy outlines how we handle your data, ensuring a secure and transparent commerce experience 
                        within our ecosystem.
                    </p>
                </div>

                <hr style={{ margin: '3.5rem 0', opacity: 0.05 }} />

                <section className="chafadi-policy-section">
                    <h2><ShoppingBag size={28} color="#22d3ee" /> 1. Overview of Data Governance</h2>
                    <p>
                        Our primary principle is simple: <span className="chafadi-highlight">We do not sell your personal information.</span> 
                        We only collect what is necessary to facilitate trade, security, and verification within the Nigerien market.
                    </p>
                </section>

                <section className="chafadi-policy-section">
                    <h2><Info size={28} color="#22d3ee" /> 2. Information We Collect</h2>
                    <p>To operate a functional marketplace, we collect the following:</p>
                    
                    <h3>2.1 Account Information</h3>
                    <ul className="chafadi-policy-list">
                        <li>Full Name and Contact Details</li>
                        <li>Email Address and Phone Number</li>
                        <li>Shipping and Billing Addresses</li>
                        <li>Profile Images and Business Documentation (for Sellers)</li>
                    </ul>

                    <h3>2.2 Transactional Data</h3>
                    <ul className="chafadi-policy-list">
                        <li>Order history and purchase behavior</li>
                        <li>Payment status (processed through secure gateways)</li>
                        <li>Communication between buyers and sellers</li>
                    </ul>

                    <h3>2.3 Technical & Usage Data</h3>
                    <ul className="chafadi-policy-list">
                        <li>Device identifiers and IP addresses</li>
                        <li>App interaction logs and performance analytics</li>
                        <li>Geolocation (for finding nearby stores and delivery tracking)</li>
                    </ul>
                </section>

                <section className="chafadi-policy-section">
                    <h2><Share2 size={28} color="#22d3ee" /> 3. Information Sharing (Public vs. Private)</h2>
                    <p>Chafadi is a marketplace, which requires certain information to be shared to enable trust:</p>

                    <h3>3.1 Public Marketplace Information</h3>
                    <p>The following information is visible to all users to facilitate commerce:</p>
                    <ul className="chafadi-policy-list">
                        <li><MapPin size={16} className="inline mr-2" /> <strong>Store Location:</strong> The physical or operational area of a seller.</li>
                        <li><UserCheck size={16} className="inline mr-2" /> <strong>Seller ID & Name:</strong> Unique identifiers for business verification.</li>
                        <li><strong>Store Ratings & Reviews:</strong> Feedback provided by other users.</li>
                        <li><strong>Product Listings:</strong> Descriptive details and pricing.</li>
                    </ul>

                    <h3>3.2 Private Data Protection</h3>
                    <p>We do <span className="chafadi-highlight">NOT</span> share your private email, password, or precise private residential address with other users or third-party marketing companies.</p>
                </section>

                <section className="chafadi-policy-section">
                    <h2><ShieldCheck size={28} color="#22d3ee" /> 4. Data Usage</h2>
                    <p>We use your information strictly for:</p>
                    <ul className="chafadi-policy-list">
                        <li>Processing orders and facilitating deliveries</li>
                        <li>Verifying seller authenticity and preventing fraud</li>
                        <li>Improving the Chafadi platform algorithm and search relevancy</li>
                        <li>Providing customer support and dispute resolution</li>
                    </ul>
                </section>

                <section className="chafadi-policy-section">
                    <h2><Lock size={28} color="#22d3ee" /> 5. Security Architecture</h2>
                    <p>
                        Your data is stored using industry-standard encryption protocols. We use secure cloud infrastructure 
                        to ensure that your sensitive information remains accessible only to authorized personnel and systems.
                    </p>
                </section>

                <section className="chafadi-policy-section">
                    <h2><Globe size={28} color="#22d3ee" /> 6. Third-Party Integrations</h2>
                    <p>
                        Chafadi integrates with trusted service providers to enhance your experience:
                    </p>
                    <ul className="chafadi-policy-list">
                        <li><strong>Google Analytics/Firebase:</strong> For performance monitoring.</li>
                        <li><strong>Payment Processors:</strong> To handle secure financial transactions.</li>
                        <li><strong>Maps Services:</strong> To provide accurate store locations.</li>
                    </ul>
                </section>

                <section className="chafadi-policy-section">
                    <h2><Mail size={28} color="#22d3ee" /> 7. Contact & Rights</h2>
                    <p>Nigerien users have the right to access, correct, or delete their data at any time.</p>
                    <ul className="chafadi-policy-list" style={{ marginTop: '1rem' }}>
                        <li>📧 Support: <a href="mailto:support@chafadi.com" style={{ color: '#22d3ee', textDecoration: 'none' }}>support@chafadi.com</a></li>
                        <li>🌐 Website: <a href="https://chafadi.com" target="_blank" rel="noopener noreferrer" style={{ color: '#22d3ee', textDecoration: 'none' }}>chafadi.com</a></li>
                    </ul>
                </section>

                <div style={{ marginTop: '5rem', textAlign: 'center', opacity: 0.3, fontSize: '0.85rem' }}>
                    © 2026 Chafadi. Operated by Quivix Digital Solutions.
                    <br />
                    Niamey, Niger
                </div>
            </motion.div>
        </div>
    );
};

export default ChafadiPrivacyPolicyPage;
